import { useState, useMemo } from 'react'
import {
  Modal,
  Button,
  Typography,
  Flex,
  Space,
  Tag,
  Divider,
  Radio,
  Alert,
} from 'antd'
import {
  InfoCircleOutlined,
  SendOutlined,
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { useStore } from 'effector-react'

import {
  submitMissingInformation,
  missingInformationTransactionRequest,
} from './model'
import {
  buttonColorTransactionRequestStatus,
  missingInformationReason,
  transactionRequestDestinationType,
  transactionRequestStatus,
} from '../../../const'
import { WithdrawalEdit } from '../../edit'
import { useUserProfile } from '../../../../../features/user'
import {
  ReasonAutoComplete,
  commitReason,
} from '../../../../../shared/reasonAutocomplete'

const { Text } = Typography

type Props = {
  request: WithdrawalEdit
  className?: string
  color?: string
  disabled?: boolean
}

type ReasonDef = {
  id: string
  key: string
  label: string
  type: number
  fields: { key: string; label: string }[]
  isCustom: boolean
  hasMoneyHoneyComment: boolean
}

function parseFields(label: string): { key: string; label: string }[] {
  const fields: { key: string; label: string }[] = []
  const regex = /\{(.*?)\}/g
  let match: RegExpExecArray | null
  while ((match = regex.exec(label)) !== null) {
    fields.push({
      key: match[1],
      label: match[1].replace(/_/g, ' '),
    })
  }
  return fields
}

function buildPreview(
  label: string,
  values: Record<string, string>,
): string {
  return label.replace(
    /\{(.*?)\}/g,
    (_, key) => values[key] || `[${key.replace(/_/g, ' ')}]`,
  )
}

const REASONS: ReasonDef[] = Object.entries(missingInformationReason).map(
  ([name, fn]) => {
    const config = fn()
    return {
      id: name,
      key: config.key,
      label: config.label,
      type: config.type,
      fields: parseFields(config.label),
      isCustom: name === 'custom',
      hasMoneyHoneyComment: name === 'contactSupport',
    }
  },
)

function MetaItem({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  if (value === null || value === undefined || value === '') return null
  return (
    <Flex align="baseline" gap={4}>
      <Text
        type="secondary"
        style={{ fontSize: 14, whiteSpace: 'nowrap' }}
      >
        {label}:
      </Text>
      <Text style={{ fontSize: 16 }}>
        {typeof value === 'boolean'
          ? value
            ? 'Yes'
            : 'No'
          : value}
      </Text>
    </Flex>
  )
}

function ReasonCard({
  reason,
  selected,
  onSelect,
  values,
  onChange,
}: {
  reason: ReasonDef
  selected: string | null
  onSelect: (id: string) => void
  values: Record<string, string>
  onChange: (key: string, val: string) => void
}) {
  const isSelected = selected === reason.id
  const border = isSelected ? '1.5px solid var(--crm-accent-blue, #3b82f6)' : '1px solid var(--crm-border)'
  const bg = isSelected ? 'var(--crm-bg-elevated)' : 'var(--crm-bg)'
  const preview = isSelected ? buildPreview(reason.label, values) : null

  return (
    <div
      onClick={() => onSelect(reason.id)}
      style={{
        border,
        borderRadius: 8,
        padding: '12px 14px',
        background: bg,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
      }}
    >
      <Flex align="flex-start" gap={10}>
        <Radio
          checked={isSelected}
          style={{ marginTop: 2, flexShrink: 0 }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <Text strong style={{ fontSize: 16 }}>
            {reason.isCustom
              ? 'Custom reason'
              : reason.label.replace(/\{.*?\}/g, '___')}
          </Text>

          {isSelected && reason.fields.length > 0 && (
            <div style={{ marginTop: 10 }}>
              {reason.fields.map((f) => (
                <div key={f.key} style={{ marginBottom: 8 }}>
                  <Text
                    type="secondary"
                    style={{
                      fontSize: 11,
                      display: 'block',
                      marginBottom: 3,
                    }}
                  >
                    {f.label}
                  </Text>
                  {reason.isCustom ? (
                    <ReasonAutoComplete
                      scope="withdrawal-missing-custom"
                      multiline
                      rows={3}
                      placeholder="Enter custom reason"
                      value={values[f.key] || ''}
                      onChange={(val) => onChange(f.key, val)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus
                      style={{ fontFamily: 'monospace' }}
                    />
                  ) : (
                    <ReasonAutoComplete
                      scope={`withdrawal-missing-${reason.key}-${f.key}`}
                      size="small"
                      placeholder={f.label}
                      value={values[f.key] || ''}
                      onChange={(val) => onChange(f.key, val)}
                      onClick={(e) => e.stopPropagation()}
                      autoFocus={f.key === reason.fields[0].key}
                      style={{ fontFamily: 'monospace' }}
                    />
                  )}
                </div>
              ))}
            </div>
          )}

          {isSelected && reason.hasMoneyHoneyComment && (
            <div style={{ marginTop: 10 }}>
              <Text
                type="secondary"
                style={{
                  fontSize: 11,
                  display: 'block',
                  marginBottom: 3,
                }}
              >
                Comment to Money Honey chat
              </Text>
              <ReasonAutoComplete
                scope="withdrawal-money-honey-comment"
                multiline
                rows={2}
                placeholder="Enter comment..."
                value={values['__moneyHoneyComment'] || ''}
                onChange={(val) => onChange('__moneyHoneyComment', val)}
                onClick={(e) => e.stopPropagation()}
                style={{ fontFamily: 'monospace' }}
              />
            </div>
          )}

          {isSelected && (
            <div
              style={{
                marginTop: 10,
                background: 'rgba(59,130,246,0.06)',
                border: '1px solid rgba(59,130,246,0.2)',
                borderRadius: 6,
                padding: '8px 10px',
              }}
            >
              <Flex
                align="center"
                gap={6}
                style={{ marginBottom: 4 }}
              >
                <SendOutlined
                  style={{ color: '#3b82f6', fontSize: 11 }}
                />
                <Text
                  type="secondary"
                  style={{
                    fontSize: 10,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                  }}
                >
                  Message to client
                </Text>
              </Flex>
              <Text style={{ fontSize: 14, fontStyle: 'italic' }}>
                "{preview}"
              </Text>
            </div>
          )}
        </div>
      </Flex>
    </div>
  )
}

// Default values for auto-populating fields from request data
function getDefaultValues(
  reasonId: string,
  request: WithdrawalEdit,
): Record<string, string> {
  if (reasonId === 'confirmYourCryptoWallet') {
    return {
      wallet: request.formData?.address ?? '',
      gateway_name: request.gateway?.name ?? '',
    }
  }
  return {}
}

export const MissingInformationModal = ({
  request,
  className,
  disabled,
}: Props) => {
  const isLoading = useStore(missingInformationTransactionRequest.pending)
  const userProfile = useUserProfile()
  const [isVisible, setVisible] = useState(false)
  const [selected, setSelected] = useState<string | null>(null)
  const [fieldVals, setFieldVals] = useState<Record<string, string>>({})
  const [error, setError] = useState('')

  const handleSelect = (id: string) => {
    if (id === selected) return
    setSelected(id)
    setFieldVals(getDefaultValues(id, request))
    setError('')
  }

  const handleField = (key: string, val: string) => {
    setFieldVals((prev) => ({ ...prev, [key]: val }))
  }

  const handleClose = () => {
    setVisible(false)
    setSelected(null)
    setFieldVals({})
    setError('')
  }

  const selectedReason = useMemo(
    () => REASONS.find((r) => r.id === selected),
    [selected],
  )

  const canSubmit = useMemo(() => {
    if (!selectedReason) return false
    const fieldsOk = selectedReason.fields.every(
      (f) => fieldVals[f.key]?.trim(),
    )
    if (
      selectedReason.hasMoneyHoneyComment &&
      !fieldVals['__moneyHoneyComment']?.trim()
    ) {
      return false
    }
    return fieldsOk
  }, [selectedReason, fieldVals])

  const handleSubmit = () => {
    if (!selectedReason) {
      setError('Please select a reason')
      return
    }
    if (!canSubmit) return

    let reason: string

    if (selectedReason.isCustom) {
      reason = fieldVals['custom'] || ''
      commitReason('withdrawal-missing-custom', reason)
    } else {
      const params: Record<string, string> = {}
      selectedReason.fields.forEach((f) => {
        const v = fieldVals[f.key] || ''
        params[`{${f.key}}`] = v
        commitReason(
          `withdrawal-missing-${selectedReason.key}-${f.key}`,
          v,
        )
      })
      reason = JSON.stringify({ key: selectedReason.key, params })
    }

    if (selectedReason.hasMoneyHoneyComment) {
      commitReason(
        'withdrawal-money-honey-comment',
        fieldVals['__moneyHoneyComment'] || '',
      )
    }

    submitMissingInformation({
      requestId: request.transactionId,
      rejectReason: reason,
      missingInfoType: selectedReason.type,
      managerEmail: userProfile.email,
      managerComment: selectedReason.hasMoneyHoneyComment
        ? fieldVals['__moneyHoneyComment'] || ''
        : '',
    })

    handleClose()
  }

  const purseLabel =
    request.destinationType ===
    transactionRequestDestinationType.wallet()
      ? 'Wallet ID'
      : 'Account ID'

  const btnColors = buttonColorTransactionRequestStatus(
    transactionRequestStatus.missingInformation(),
  )

  return (
    <>
      <Button
        type="primary"
        style={{
          color: btnColors.text,
          backgroundColor: btnColors.background,
        }}
        className={className}
        onClick={() => setVisible(true)}
        disabled={disabled}
      >
        Missing Information
      </Button>
      <Modal
        open={isVisible}
        onCancel={handleClose}
        title={
          <Flex align="center" gap={8}>
            <InfoCircleOutlined style={{ color: '#f59e0b' }} />
            <span>Set missing information reason</span>
          </Flex>
        }
        width={900}
        zIndex={1050}
        footer={
          <Flex justify="space-between" align="center">
            <Text type="secondary" style={{ fontSize: 11 }}>
              {selected
                ? 'Review the message preview before sending'
                : 'Select a reason to continue'}
            </Text>
            <Space>
              <Button onClick={handleClose}>Cancel</Button>
              <Button
                type="primary"
                disabled={!canSubmit || isLoading}
                loading={isLoading}
                icon={<SendOutlined />}
                onClick={handleSubmit}
              >
                Send
              </Button>
            </Space>
          </Flex>
        }
        styles={{
          body: {
            padding: '16px 24px',
            maxHeight: '70vh',
            overflowY: 'auto',
          },
          header: { paddingBottom: 12 },
        }}
      >
        {/* Transaction meta */}
        <div
          style={{
            background: 'var(--crm-bg-elevated)',
            border: '1px solid var(--crm-border)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 16,
          }}
        >
          <Flex gap={24} style={{ marginBottom: 8 }}>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Created
              </Text>
              <Text style={{ fontSize: 16 }}>
                {dayjs(request.createdAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
            <Flex gap={4} align="baseline">
              <Text
                type="secondary"
                style={{
                  fontSize: 10,
                  fontWeight: 600,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                }}
              >
                Updated
              </Text>
              <Text style={{ fontSize: 16 }}>
                {dayjs(request.updatedAt).format('DD MMM YYYY, HH:mm')}
              </Text>
            </Flex>
          </Flex>

          <Divider style={{ margin: '8px 0' }} />

          <Flex gap={20} wrap="wrap">
            <MetaItem label="ID" value={request.transactionId} />
            <MetaItem
              label="Client ID"
              value={request.client.clientId}
            />
            <MetaItem label={purseLabel} value={request.destinationId} />
            <MetaItem
              label="Processing type"
              value={
                <Tag
                  color={request.isAuto ? 'green' : 'geekblue'}
                  style={{ fontSize: 11 }}
                >
                  {request.isAuto ? 'Auto' : 'Manual'}
                </Tag>
              }
            />
          </Flex>

          <Divider style={{ margin: '8px 0' }} />
          <Flex gap={16} wrap="wrap">
            <MetaItem
              label="clientPurseId"
              value={request.clientPurseId}
            />
            {request.clientPurseId != 0 && (
              <>
                <MetaItem
                  label="isCard"
                  value={Boolean(
                    request.clientPurse?.isCard,
                  ).toString()}
                />
                <MetaItem
                  label="expiry"
                  value={request.clientPurse?.expiry}
                />
                <MetaItem
                  label="mask"
                  value={request.clientPurse?.mask}
                />
                <MetaItem
                  label="type"
                  value={request.clientPurse?.type}
                />
              </>
            )}
          </Flex>
        </div>

        {error && (
          <Alert
            message={error}
            type="error"
            style={{ marginBottom: 12 }}
          />
        )}

        <Text
          type="secondary"
          style={{
            fontSize: 11,
            fontWeight: 600,
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            display: 'block',
            marginBottom: 10,
          }}
        >
          Missing information reason
        </Text>

        <Flex vertical gap={8}>
          {REASONS.map((reason) => (
            <ReasonCard
              key={reason.id}
              reason={reason}
              selected={selected}
              onSelect={handleSelect}
              values={fieldVals}
              onChange={handleField}
            />
          ))}
        </Flex>
      </Modal>
    </>
  )
}
